import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  ChevronsUpDown, Clock, HardDriveDownload, Laptop, FolderPlus, FolderUp, Globe, HardDrive, LogOut, Moon, Plus, Search, Star, Sun, Trash2,
  Upload, UserCog, Users,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthProvider';
import { useDrive } from '../../contexts/DriveProvider';
import driveApi, { errorMessage, ROLES } from '../../api/drive';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { initials } from '../../lib/files';
import NameDialog from './NameDialog';
import UploadPanel from './UploadPanel';
import Toaster from './Toaster';
import CommandPalette from './CommandPalette';
import { filesFromInput } from './dropFiles';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuTrigger } from '../ui/context-menu';
import { DRIVE_EVENTS } from './events';

/** LampTwist Drive mark: one filament line runs through a file and leaves it as light (sharing). */
export const LampMark = ({ size = 28, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden="true">
    <defs>
      <clipPath id="lt-in"><rect x="0" y="0" width="46" height="64" /></clipPath>
      <clipPath id="lt-out"><rect x="46" y="0" width="18" height="64" /></clipPath>
    </defs>
    <rect width="64" height="64" rx="15" fill="#171717" />
    <path d="M14 19c0-3.3 2.7-6 6-6h15.5L46 23.5V45c0 3.3-2.7 6-6 6H20c-3.3 0-6-2.7-6-6z" fill="#FAFAFA" />
    <path d="M35.5 13v6.5c0 2.2 1.8 4 4 4H46z" fill="#F5A524" />
    <path d="M21 45C21 39 26 37.5 30 36.5C35.5 35 36.5 29 33 26.6C29.6 24.3 25 27 26.4 31C27.8 35 33.5 34.6 37.5 31.8C40.6 29.6 43.5 27.8 49.5 27.4" fill="none" stroke="#171717" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" clipPath="url(#lt-in)" />
    <path d="M21 45C21 39 26 37.5 30 36.5C35.5 35 36.5 29 33 26.6C29.6 24.3 25 27 26.4 31C27.8 35 33.5 34.6 37.5 31.8C40.6 29.6 43.5 27.8 49.5 27.4" fill="none" stroke="#F5A524" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" clipPath="url(#lt-out)" />
    <path d="M49.5 22.6L57 27.2L49.8 32.1z" fill="#F5A524" />
  </svg>
);

export const useDarkMode = () => {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    try { localStorage.setItem('theme', dark ? 'dark' : 'light'); } catch { /* storage blocked */ }
  }, [dark]);
  return [dark, setDark];
};


/** `listen`: answers "New folder / Upload" requests from ⌘K and right-click menus. Only one instance may. */
const NewButton = ({ listen = false }) => {
  const { location, personalDrive, uploadFiles, bump, toast } = useDrive();
  const navigate = useNavigate();
  const filesRef = useRef(null);
  const folderRef = useRef(null);
  const [folderOpen, setFolderOpen] = useState(false);

  // Fall back to My Drive when the open view isn't a folder you can add to
  const target = location && location.role >= ROLES.EDITOR
    ? location
    : personalDrive && { driveId: personalDrive.id, folderId: null };

  useEffect(() => {
    if (!listen) return undefined;
    const open = () => setFolderOpen(true);
    const files = () => filesRef.current?.click();
    const folder = () => folderRef.current?.click();
    window.addEventListener(DRIVE_EVENTS.newFolder, open);
    window.addEventListener(DRIVE_EVENTS.uploadFiles, files);
    window.addEventListener(DRIVE_EVENTS.uploadFolder, folder);
    return () => {
      window.removeEventListener(DRIVE_EVENTS.newFolder, open);
      window.removeEventListener(DRIVE_EVENTS.uploadFiles, files);
      window.removeEventListener(DRIVE_EVENTS.uploadFolder, folder);
    };
  }, [listen]);

  const onPick = (e) => {
    const files = filesFromInput(e.target.files);
    e.target.value = '';
    if (!files.length || !target) return;
    uploadFiles(files, { drive: target.driveId, parent: target.folderId });
    if (target !== location) navigate(`/drive/${target.driveId}`);
  };

  const createFolder = async (name) => {
    try {
      await driveApi.createFolder(target.driveId, target.folderId, name);
      bump();
      if (target !== location) navigate(`/drive/${target.driveId}`);
      return true;
    } catch (err) {
      toast(errorMessage(err, 'Could not create the folder.'), 'error');
      return false;
    }
  };

  return (
    <>
      {/* Non-modal so the New folder dialog it opens doesn't leave the page unclickable */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={!target}
            className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-filament px-3.5 text-[13.5px] font-semibold text-filament-foreground transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 md:w-full"
          >
            <Plus className="h-4 w-4" strokeWidth={2.4} /> New
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem className="gap-2 py-2" onSelect={() => setFolderOpen(true)}>
            <FolderPlus className="h-4 w-4" /> New folder
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="gap-2 py-2" onSelect={() => filesRef.current?.click()}>
            <Upload className="h-4 w-4" /> Upload files
          </DropdownMenuItem>
          <DropdownMenuItem className="gap-2 py-2" onSelect={() => folderRef.current?.click()}>
            <FolderUp className="h-4 w-4" /> Upload folder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input ref={filesRef} type="file" multiple hidden onChange={onPick} />
      <input ref={folderRef} type="file" hidden webkitdirectory="" onChange={onPick} />
      <NameDialog
        open={folderOpen}
        onOpenChange={setFolderOpen}
        title="New folder"
        initial="Untitled folder"
        confirmLabel="Create"
        onSubmit={createFolder}
      />
    </>
  );
};

const navClass = ({ isActive }) =>
  `flex h-8 shrink-0 items-center gap-2.5 whitespace-nowrap rounded-md px-2.5 text-[13.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
    isActive ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
  }`;

/** Drop dragged files/folders on a drive in the sidebar to move them to its top level. */
const useDriveDrop = (drive, label) => {
  const { bump, toast } = useDrive();
  if (!drive) return {};
  return {
    onDragOver: (e) => {
      if (e.dataTransfer.types.includes('application/x-drive-nodes')) {
        e.preventDefault();
        e.currentTarget.dataset.dropping = 'true';
      }
    },
    onDragLeave: (e) => { delete e.currentTarget.dataset.dropping; },
    onDrop: async (e) => {
      delete e.currentTarget.dataset.dropping;
      const raw = e.dataTransfer.getData('application/x-drive-nodes');
      if (!raw) return;
      e.preventDefault();
      const ids = JSON.parse(raw);
      try {
        await Promise.all(ids.map((id) => driveApi.move(id, null, drive.id)));
        toast(ids.length > 1 ? `Moved ${ids.length} items to “${label}”` : `Moved to “${label}”`);
        bump();
      } catch (err) {
        toast(errorMessage(err, 'Could not move.'), 'error');
        bump();
      }
    },
  };
};

// Forwards ref + props so a context menu can wrap it (shared drives, for admins)
const NavItem = React.forwardRef(({ to, icon: Icon, swatch, children, drop, ...rest }, ref) => (
  <NavLink
    ref={ref}
    to={to}
    className={(state) => `${navClass(state)} data-[dropping=true]:bg-accent data-[dropping=true]:outline data-[dropping=true]:outline-2 data-[dropping=true]:outline-filament`}
    {...drop}
    {...rest}
  >
    {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
    {swatch && <span className="mx-[3px] h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: swatch }} aria-hidden="true" />}
    <span className="truncate">{children}</span>
  </NavLink>
));
NavItem.displayName = 'NavItem';

const DropNavItem = React.forwardRef(({ drive, label, ...props }, ref) => {
  const drop = useDriveDrop(drive, label);
  return <NavItem ref={ref} drop={drop} {...props} />;
});
DropNavItem.displayName = 'DropNavItem';

// Same swatches as teams: dark enough for white text, distinct side by side
const DRIVE_COLORS = ['#B4530A', '#0E6B63', '#3B4BA8', '#7A2348', '#4A4740', '#5B2C7A'];

/** Admins: right-click a shared drive in the sidebar to recolour it. */
const DriveColorMenu = ({ drive, children }) => {
  const { refreshDrives, toast } = useDrive();
  const recolor = async (color) => {
    try {
      await driveApi.updateDrive(drive.id, { color });
      await refreshDrives();
      toast(`Changed the colour of “${drive.name}”`);
    } catch (err) {
      toast(errorMessage(err, 'Could not change the colour.'), 'error');
    }
  };
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuLabel>Colour of “{drive.name}”</ContextMenuLabel>
        <div role="group" aria-label="Drive colour" className="flex gap-1 px-1 pb-1">
          {DRIVE_COLORS.map((c) => {
            const current = (drive.color || '').toUpperCase() === c;
            return (
              <ContextMenuItem
                key={c}
                aria-label={`Colour ${c}${current ? ' (current)' : ''}`}
                onSelect={() => !current && recolor(c)}
                className="h-8 w-8 justify-center p-0"
              >
                <span
                  className={`h-5 w-5 rounded-[5px] ${current ? 'ring-2 ring-foreground ring-offset-2 ring-offset-popover' : ''}`}
                  style={{ background: c }}
                  aria-hidden="true"
                />
              </ContextMenuItem>
            );
          })}
        </div>
      </ContextMenuContent>
    </ContextMenu>
  );
};

const SectionLabel = ({ children, action }) => (
  <div className="hidden h-7 items-center justify-between px-2.5 md:flex">
    <span className="text-xs font-medium text-muted-foreground">{children}</span>
    {action}
  </div>
);

const AccountMenu = ({ compact = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useDarkMode();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className={`flex items-center gap-2.5 rounded-md text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${compact ? 'p-0.5' : 'w-full px-2 py-1.5'}`}
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={user?.clerkImageUrl || undefined} alt="" />
            <AvatarFallback className="bg-primary text-[11px] font-semibold text-primary-foreground">{initials(user?.displayName)}</AvatarFallback>
          </Avatar>
          {!compact && (
            <>
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate text-[13px] font-medium">{user?.displayName}</span>
                <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={compact ? 'end' : 'start'} side={compact ? 'bottom' : 'top'} className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="font-medium">{user?.displayName}</div>
          <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/desktop')} className="gap-2">
          <Laptop className="h-4 w-4" /> Desktop app &amp; computers
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setDark((d) => !d)} className="gap-2">
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {dark ? 'Switch to light mode' : 'Switch to dark mode'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => logout()} className="gap-2">
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SearchTrigger = ({ onOpen, compact = false }) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label="Search and jump to (⌘K)"
    className={`flex h-8 items-center gap-2 rounded-md border border-border bg-background text-[13px] text-muted-foreground transition-colors hover:border-input hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${compact ? 'w-8 justify-center' : 'w-full px-2.5'}`}
  >
    <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    {!compact && (
      <>
        <span className="flex-1 text-left">Search…</span>
        <kbd className="rounded border border-border px-1 text-[11px] font-medium">⌘K</kbd>
      </>
    )}
  </button>
);

const Sidebar = ({ onSearch }) => {
  const { personalDrive, sharedDrives, refreshDrives, toast } = useDrive();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [driveOpen, setDriveOpen] = useState(false);

  const createDrive = async (name) => {
    try {
      const drive = await driveApi.createDrive(name);
      await refreshDrives();
      navigate(`/drive/${drive.id}`);
      return true;
    } catch (err) {
      toast(errorMessage(err, 'Could not create the drive.'), 'error');
      return false;
    }
  };

  return (
    <aside className="flex w-full flex-col border-b border-border bg-sidebar md:sticky md:top-0 md:h-screen md:w-[248px] md:shrink-0 md:border-b-0 md:border-r">
      {/* Brand row (phones: also search + account) */}
      <div className="flex h-14 items-center gap-2.5 px-4 md:px-3.5">
        <LampMark size={26} className="rounded-[6px] dark:ring-1 dark:ring-border" />
        <span className="text-[14.5px] font-semibold tracking-[-0.01em]">
          LampTwist <span className="font-normal text-muted-foreground">Drive</span>
        </span>
        <div className="ml-auto flex items-center gap-2 md:hidden">
          <SearchTrigger onOpen={onSearch} compact />
          <AccountMenu compact />
        </div>
      </div>

      <nav
        aria-label="Drive sections"
        className="relative flex items-center gap-1 overflow-x-auto px-4 pb-3 md:flex-1 md:flex-col md:items-stretch md:gap-5 md:overflow-y-auto md:px-2.5 md:pb-3"
      >
        <div className="hidden flex-col gap-2 px-1 md:flex">
          <SearchTrigger onOpen={onSearch} />
          <NewButton listen />
        </div>
        <div className="mr-1 md:hidden"><NewButton /></div>

        <div className="flex shrink-0 gap-0.5 md:flex-col">
          {personalDrive && <DropNavItem drive={personalDrive} label="My Drive" to={`/drive/${personalDrive.id}`} icon={HardDrive}>My Drive</DropNavItem>}
          <NavItem to="/shared" icon={Users}>Shared with me</NavItem>
          <NavItem to="/recent" icon={Clock}>Recent</NavItem>
          <NavItem to="/starred" icon={Star}>Starred</NavItem>
          <NavItem to="/links" icon={Globe}>External links</NavItem>
          <NavItem to="/trash" icon={Trash2}>Trash</NavItem>
        </div>

        <div className="flex shrink-0 gap-0.5 md:flex-col">
          <SectionLabel
            action={user?.is_staff && (
              <button
                type="button"
                onClick={() => setDriveOpen(true)}
                aria-label="New shared drive"
                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            )}
          >
            Shared drives
          </SectionLabel>
          {sharedDrives.length === 0 && <p className="hidden px-2.5 text-[13px] text-muted-foreground md:block">None yet</p>}
          {sharedDrives.map((d) => (user?.is_staff
            ? <DriveColorMenu key={d.id} drive={d}><DropNavItem drive={d} label={d.name} to={`/drive/${d.id}`} swatch={d.color}>{d.name}</DropNavItem></DriveColorMenu>
            : <DropNavItem key={d.id} drive={d} label={d.name} to={`/drive/${d.id}`} swatch={d.color}>{d.name}</DropNavItem>))}
        </div>

        {user?.is_staff && (
          <div className="flex shrink-0 gap-0.5 md:flex-col">
            <SectionLabel>Admin</SectionLabel>
            <NavItem to="/admin/people" icon={UserCog}>People &amp; teams</NavItem>
            <NavItem to="/admin/usage" icon={HardDriveDownload}>Storage &amp; usage</NavItem>
          </div>
        )}
      </nav>

      <div className="hidden border-t border-border p-2 md:block">
        <AccountMenu />
      </div>

      <NameDialog
        open={driveOpen}
        onOpenChange={setDriveOpen}
        title="New shared drive"
        description="You'll be its manager. Add teams with the drive's Share button."
        initial=""
        confirmLabel="Create drive"
        onSubmit={createDrive}
      />
    </aside>
  );
};

const AppShell = () => {
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground md:flex-row">
      <Sidebar onSearch={() => setPaletteOpen(true)} />
      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <UploadPanel />
      <Toaster />
    </div>
  );
};

export default AppShell;
